import { OptJobStatus, OptJobType } from 'api/optJobs/optJobs.api'
import { toOptJobOutcome } from 'ui/optJobOutcome/optJobOutcome.helper'
import { OptJobOutcome } from 'ui/optJobOutcome/optJobOutcome.types'
import { StatusIconVariants } from './StatusIcon'

type OptJobIconFamily = Record<OptJobOutcome, StatusIconVariants>

/** Job types with no iconography of their own still have to show their outcome. */
const GENERIC: OptJobIconFamily = {
  created: 'spinner',
  finished: 'generic_finished',
  failed: 'generic_failed',
  unsolvable: 'generic_unsolvable',
}
const REGULAR: OptJobIconFamily = {
  created: 'regular_created',
  finished: 'regular_finished',
  failed: 'regular_failed',
  unsolvable: 'regular_unsolvable',
}
const REGULAR_WIP: OptJobIconFamily = {
  created: 'spinner',
  finished: 'regular_wip_inactive',
  failed: 'regular_wip_failed',
  unsolvable: 'generic_unsolvable',
}
/** Publishing a plan is its own act, and shows the rocket until it succeeds or does not. */
const PUBLISHED_PLAN: OptJobIconFamily = {
  created: 'regular_wip_created',
  finished: 'regular_wip_published',
  failed: 'regular_wip_failed',
  unsolvable: 'regular_wip_failed',
}
const FUEL_PLAN: OptJobIconFamily = {
  created: 'fuelplan_created',
  finished: 'fuelplan_finished',
  failed: 'fuelplan_failed',
  unsolvable: 'generic_unsolvable',
}
const FOLLOWUP: OptJobIconFamily = {
  created: 'followup_created',
  finished: 'followup_finished',
  failed: 'followup_failed',
  unsolvable: 'followup_unsolvable',
}
const SANDBOX: OptJobIconFamily = {
  created: 'sandbox_created',
  finished: 'sandbox_finished',
  failed: 'sandbox_failed',
  unsolvable: 'generic_unsolvable',
}
const INTRADAY: OptJobIconFamily = {
  created: 'intraday_created',
  finished: 'intraday_published',
  failed: 'intraday_failed',
  unsolvable: 'generic_unsolvable',
}
const MEASVALUES: OptJobIconFamily = {
  created: 'meas_created',
  finished: 'meas_finished',
  failed: 'meas_failed',
  unsolvable: 'generic_unsolvable',
}

/**
 * Exhaustive by type, so a new `OptJobType` is a compile error here rather than a
 * job silently rendering as one still spinning. Point a type at `GENERIC` to say
 * "no iconography of its own yet" deliberately.
 */
const ICONS_BY_OPT_JOB_TYPE: Record<OptJobType, OptJobIconFamily> = {
  regular: REGULAR,
  regular_wip: REGULAR_WIP,
  followup_with_deviations: FOLLOWUP,
  followup_without_deviations: FOLLOWUP,
  followup_regular: FOLLOWUP,
  measvalues_calculations: MEASVALUES,
  real_time_measvalues_calculations: INTRADAY,
  real_time: INTRADAY,
  intraday_plan: INTRADAY,
  fuel_plan: FUEL_PLAN,
  sandbox: SANDBOX,
  marginal_cost_calculation: GENERIC,
  electricity_price_scenario: GENERIC,
  settlement: GENERIC,
}

/**
 * With `use_draftproductionplan` the two plan types trade places: `regular_wip` is the
 * everyday plan optimization, and `regular` is only the act of publishing its result.
 * Their iconography follows them across.
 */
const DRAFT_PLAN_ICON_OVERRIDES: Partial<Record<OptJobType, OptJobIconFamily>> = {
  regular: PUBLISHED_PLAN,
  regular_wip: REGULAR,
}

export function getStatusIconVariant(
  optJobType: OptJobType,
  status: OptJobStatus,
  treatRegularAsPublished: boolean
): StatusIconVariants {
  // `opt_job_type` is parsed as a plain string and cast, not validated against the
  // union, so a job type the backend has and this client does not can reach here.
  const family =
    (treatRegularAsPublished ? DRAFT_PLAN_ICON_OVERRIDES[optJobType] : undefined) ??
    ICONS_BY_OPT_JOB_TYPE[optJobType] ??
    GENERIC

  return family[toOptJobOutcome(status, treatRegularAsPublished && optJobType === 'regular')]
}
